<template>
    <div class="about-page">
        <!-- Hero / Intro Section -->
        <Section class="about-hero-section">
            <div class="about-hero-container">
                <div class="sloth-emoji-large">🦥</div>
                <h1>{{ t('about.title') }}</h1>
                <p class="about-intro">{{ t('about.intro') }}</p>
            </div>
        </Section>

        <!-- Philosophy Section -->
        <Section class="philosophy-section">
            <v-row align="center">
                <v-col
                    cols="12"
                    md="6"
                >
                    <h2>{{ t('about.philosophy_title') }}</h2>
                    <p class="philosophy-text">{{ t('about.philosophy') }}</p>
                </v-col>
                <v-col
                    cols="12"
                    md="6"
                >
                    <div class="brand-image-wrap">
                        <img
                            src="/images/sloth-family.webp"
                            alt="The Slothlike team"
                            class="brand-image"
                            loading="lazy"
                        />
                    </div>
                </v-col>
            </v-row>
        </Section>

        <!-- Pillars Section -->
        <Section class="pillars-section">
            <div class="pillars-container">
                <header class="section-heading">
                    <h2>{{ t('about.pillars_title') }}</h2>
                </header>
                <div class="pillars-chips">
                    <v-chip
                        v-for="pillar in pillars"
                        :key="pillar"
                        size="large"
                        color="primary"
                        variant="tonal"
                        class="pillar-chip"
                    >
                        {{ pillar }}
                    </v-chip>
                </div>
            </div>
        </Section>
    </div>
</template>

<script setup lang="ts">
    import Section from '@/components/Section.vue'
    import { useSEO } from '@/composables/useSEO'
    import { useTranslations } from '@/composables/useTranslations'

    const { t, get } = useTranslations()
    const pillars = get<string[]>('about.pillars') ?? []

    useSEO({
        title: () => t('seo.about.title'),
        description: () => t('seo.about.description'),
    })
</script>

<style scoped>
    .about-page {
        padding-top: 80px;
    }

    /* Hero Section */
    .about-hero-section {
        text-align: center;
        background: linear-gradient(
            180deg,
            rgba(var(--v-theme-surface), 1) 0%,
            rgba(var(--v-theme-surface-variant), 0.3) 100%
        );
    }

    .about-hero-container {
        max-width: 800px;
        margin: 0 auto;
    }

    .sloth-emoji-large {
        font-size: 4rem;
        margin-bottom: 16px;
    }

    .about-hero-section h1 {
        font-size: clamp(2.5rem, 5vw, 4rem);
        font-weight: 800;
        line-height: 1.1;
        margin: 0 0 24px;
    }

    .about-intro {
        font-size: 1.25rem;
        line-height: 1.7;
        color: rgb(var(--v-theme-on-surface-variant));
        margin: 0;
    }

    /* Philosophy Section */
    .philosophy-section h2 {
        font-size: clamp(1.75rem, 3.5vw, 2.5rem);
        font-weight: 700;
        margin: 0 0 24px;
    }

    .philosophy-text {
        font-size: 1.125rem;
        line-height: 1.75;
        color: rgb(var(--v-theme-on-surface-variant));
        margin: 0;
    }

    .brand-image-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .brand-image {
        width: 100%;
        max-width: 480px;
        height: auto;
        border-radius: 16px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
    }

    /* Pillars Section */
    .pillars-section {
        background: rgba(var(--v-theme-surface-variant), 0.2);
    }

    .pillars-container {
        max-width: 900px;
        margin: 0 auto;
    }

    .section-heading {
        text-align: center;
        margin-bottom: 40px;
    }

    .section-heading h2 {
        font-size: clamp(2rem, 4vw, 3rem);
        font-weight: 700;
        margin: 0;
    }

    .pillars-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        justify-content: center;
    }

    .pillar-chip {
        font-size: 1rem;
        font-weight: 600;
    }

    @media (max-width: 960px) {
        .about-page {
            padding-top: 60px;
        }
    }
</style>
